'use client';

import { toast } from '~/utils/toast';
import HoverClickPopout from '~/components/HoverClickPopout';
import usePopover from '~/hooks/usePopover';
import { api } from '~/utils/api';
import { serverError } from '~/utils/serverError';
import { KeyRound, LogOut, UserRound, X } from 'lucide-react';
import Link from 'next/link';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import NavBarItem from './NavBarItem';
import {
  NavPopoverBody,
  NavPopoverFooter,
  NavPopoverHeader,
  NavPopoverShell,
  navPopoverFooterLinkClass,
  navPopoverIconButtonClass,
} from './NavPopoverShell';

type UserIconButtonProps = {
  isSidebarExpanded: boolean;
};

function ProfileField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-muted-foreground text-[11px] font-medium tracking-[0.08em] uppercase">
        {label}
      </div>
      <div className="text-foreground mt-1 truncate text-sm">{value}</div>
    </div>
  );
}

export default function UserIconButton({ isSidebarExpanded }: UserIconButtonProps) {
  const { mutate: changePassword } = api.user.changePassword.useMutation();
  const {
    open: openMenu,
    handleClose: handleCloseMenu,
    handleOpen: handleOpenMenu,
    anchorEl: menuAnchorEl,
  } = usePopover<HTMLDivElement>();
  const { data: userData } = api.user.authorize.useQuery();
  const fullname = [userData?.user.firstName ?? '', userData?.user.lastName ?? ''].join(' ').trim();
  const displayName = fullname || userData?.user.email?.split('@')[0] || 'Account';

  const renderUserContent = (closePopout: () => void) => (
    <NavPopoverShell width={300}>
      <NavPopoverHeader
        title="Account"
        actions={
          <button
            type="button"
            className={navPopoverIconButtonClass}
            onClick={closePopout}
            aria-label="Close"
          >
            <X className="size-3.5" />
          </button>
        }
      />
      <NavPopoverBody className="space-y-3 px-4 py-3">
        <ProfileField label="Email" value={userData?.user.email} />
        <ProfileField label="Name" value={fullname || null} />
        <div className="border-border border-t" />
        <ProfileField label="Organization" value={userData?.user.tenant.name} />
        <div className="border-border border-t" />
        <WorkspaceSwitcher />
        <button
          type="button"
          className="hover:bg-sidebar-accent/60 flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          onClick={() => {
            changePassword(undefined, {
              onSuccess: (response) => {
                toast.success(response);
                closePopout();
              },
              onError: serverError,
            });
          }}
        >
          <KeyRound className="size-4 shrink-0" />
          <span className="text-sm font-medium">Change Password</span>
        </button>
      </NavPopoverBody>
      <NavPopoverFooter>
        <Link href="/auth/logout" className={navPopoverFooterLinkClass}>
          <LogOut className="size-3.5" />
          <span>Logout</span>
        </Link>
      </NavPopoverFooter>
    </NavPopoverShell>
  );

  return (
    <HoverClickPopout
      renderContent={renderUserContent}
      isOpen={openMenu}
      anchorEl={menuAnchorEl}
      onOpen={handleOpenMenu}
      onClose={handleCloseMenu}
      width="300px"
      fullWidth
      openOnHover={false}
      offset={0}
      alignToContentPaneBottom
    >
      <NavBarItem
        label={displayName}
        icon={UserRound}
        isSidebarExpanded={isSidebarExpanded}
        asButton
        disableTooltip={isSidebarExpanded}
      />
    </HoverClickPopout>
  );
}
